import { useSortable } from "@dnd-kit/sortable";
import { ChevronRight, ClipboardCopy, LinkIcon, PanelRightOpen, Pencil, Trash2 } from "lucide-react";
import { type KeyboardEvent, memo, useEffect, useState } from "react";
import { toast } from "sonner";
import { SUBTASK_INDENT_WIDTH, TASK_ROW_HEIGHT } from "@/common/constants";
import { useTaskNavigation } from "@/hooks/useTaskNavigation";
import { cn } from "@/lib/utils";
import { useChangeTaskProperty, useDeleteTaskMutation } from "@/mutations/task-mutations";
import type { AlertDialogImperativeHandle } from "../DefaultAlertDialog";
import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuSeparator,
    ContextMenuTrigger,
} from "../ui/context-menu";
import type { ColumnDef } from "./ColumnModel";
import type { Row } from "./RowModel";
import { useIsTaskSelected, useTaskSelectionActions } from "./TaskSelectionContext";
import { useBulkTargetIds } from "./useBulkTargetIds";
import type { ReorderPosition } from "./useManualSort";

export type TaskTableTaskRowProps = {
    row: Row;
    columns: ColumnDef[];
    isFirst: boolean;
    isLast: boolean;
    onRowClick: (taskId: number, event: React.MouseEvent) => void;
    deleteTaskAlertDialogRef: React.RefObject<AlertDialogImperativeHandle | null>;
    onToggleSubtasks: (rowKey: string) => void;
    isSubtasksExpanded: boolean;
    isNestTarget: boolean;
    dropLinePosition: ReorderPosition | null;
    dropLineDepth?: number;
    isDragSource: boolean;
};

/** Rows are a fixed height, so the style object can be shared by all of them. */
const ROW_STYLE: React.CSSProperties = { height: `${TASK_ROW_HEIGHT}px` };

function TaskTableTaskRowInner({
    row,
    columns,
    onRowClick,
    deleteTaskAlertDialogRef,
    onToggleSubtasks,
    isSubtasksExpanded,
    isNestTarget,
    dropLinePosition,
    dropLineDepth,
    isDragSource,
}: TaskTableTaskRowProps) {
    const deleteMutation = useDeleteTaskMutation();
    const { change: changeTaskProperty } = useChangeTaskProperty();
    const { openTask } = useTaskNavigation();
    const { setEditingTaskId, clearSelection } = useTaskSelectionActions();
    // Subscribed per row rather than passed down, so a selection change re-renders
    // only the rows whose selected state actually flipped.
    const isSelected = useIsTaskSelected(row.data.id);
    const getTargetIds = useBulkTargetIds();

    // dnd-kit sortable — disabled for temp tasks (negative IDs)
    const isTempTask = row.data.id < 0;
    const { attributes, listeners, setNodeRef } = useSortable({
        id: row.data.id.toString(),
        disabled: isTempTask,
    });

    // Track animation state locally to prevent premature class removal
    const [showJustCreatedAnimation, setShowJustCreatedAnimation] = useState(row.data.justCreated);

    const handleRowClick = (event: React.MouseEvent) => {
        onRowClick(row.data.id, event);
    };

    useEffect(() => {
        if (row.data.justCreated) {
            // Start animation
            setShowJustCreatedAnimation(true);

            // After animation completes, update data and remove class
            const timer = setTimeout(() => {
                changeTaskProperty(row.data.id, {
                    justCreated: false,
                });
                // Remove class after a brief delay to ensure animation completes
                setTimeout(() => {
                    setShowJustCreatedAnimation(false);
                }, 100);
            }, 2000);

            return () => clearTimeout(timer);
        }
        // justCreated became false (timer fired or external update) — ensure animation is off
        setShowJustCreatedAnimation(false);
    }, [row.data.justCreated, row.data.id]);

    const handleDeleteTask = async () => {
        const ids = getTargetIds(row.data.id);
        const isBulk = ids.length > 1;
        const confirmed = await deleteTaskAlertDialogRef.current?.show(
            isBulk
                ? {
                      title: `Delete ${ids.length} tasks`,
                      description: `Are you sure you want to delete these ${ids.length} tasks? This action cannot be undone.`,
                  }
                : {
                      description: `Are you sure you want to delete task T-${row.data.number}? This action cannot be undone.`,
                  }
        );
        if (!confirmed) return;
        await Promise.all(ids.map((id) => deleteMutation.mutateAsync({ id })));
        if (isBulk) clearSelection();
    };

    const handleCopyTaskId = () => {
        const str = `T-${row.data.number}`;
        navigator.clipboard.writeText(str);
        toast.success("Task ID copied", {
            description: `The task ID has been copied to your clipboard.`,
        });
    };

    const handleCopyTaskTitle = () => {
        const str = `T-${row.data.number} ${row.data.title}`;
        navigator.clipboard.writeText(str);
        toast.success("Task title copied", {
            description: `The task title has been copied to your clipboard.`,
        });
    };

    const handleCopyTaskLink = () => {
        const tenantBaseUrl = window.location.origin;
        const url = `${tenantBaseUrl}/all?task=${row.data.id}`;
        navigator.clipboard.writeText(url);
        toast.success("Task link copied", {
            description: `The link to this task has been copied to your clipboard.`,
        });
    };

    const handleOpenInPanel = () => {
        openTask(row.data.id);
    };

    const handleEditTitle = () => {
        setEditingTaskId(row.data.id);
    };

    return (
        <ContextMenu>
            <ContextMenuTrigger
                render={
                    // biome-ignore lint/a11y/noStaticElementInteractions: need the click
                    <div
                        ref={setNodeRef}
                        {...(isTempTask ? {} : attributes)}
                        {...(isTempTask ? {} : listeners)}
                        key={row.key}
                        data-task-row=""
                        style={ROW_STYLE}
                        onClick={handleRowClick}
                        className={cn([
                            `group/row h-(--task-row-height) w-full grid transition-background duration-100 ease-out grid-cols-(--template-columns) select-none`,
                            "relative",
                            {
                                "hover:bg-zinc-200/40": !isSelected,
                                "bg-zinc-300/40 hover:bg-zinc-300/50": isSelected,
                                "just-created": showJustCreatedAnimation,
                                "opacity-50": !row.isFilterMatch,
                                "opacity-30": isDragSource,
                                "bg-blue-50 before:content-[''] before:absolute before:inset-0 before:border-l-2 before:border-blue-500 before:pointer-events-none before:z-50":
                                    isNestTarget,
                            },
                            "focus-visible:outline-none focus-visible:ring-0",
                        ])}
                        onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                // Create a synthetic mouse event for keyboard interactions
                                const syntheticEvent = {
                                    metaKey: e.metaKey,
                                    ctrlKey: e.ctrlKey,
                                    shiftKey: e.shiftKey,
                                } as React.MouseEvent;
                                handleRowClick(syntheticEvent);
                            }
                        }}
                    >
                        {/* Drop line indicator — indented based on target depth */}
                        {dropLinePosition && (
                            <div
                                className={cn(
                                    "absolute right-0 h-0.5 bg-blue-500 z-50 pointer-events-none",
                                    "before:content-[''] before:absolute before:left-0 before:-translate-y-1/2 before:top-1/2 before:w-0 before:h-0 before:border-y-[5px] before:border-y-transparent before:border-l-[5px] before:border-l-blue-500",
                                    dropLinePosition === "above" ? "top-0" : "-bottom-0.5"
                                )}
                                style={{
                                    left:
                                        (dropLineDepth ?? row.depth) > 0
                                            ? `${(dropLineDepth ?? row.depth) * SUBTASK_INDENT_WIDTH + 20}px`
                                            : "0px",
                                }}
                            />
                        )}
                        {columns.map((cell, idx) => {
                            const isFirstCol = idx === 0;
                            return (
                                <div
                                    key={`${row.key}-${cell.id}`}
                                    className={cn(
                                        "task-cell",
                                        !cell.isReadOnly && "editable-task-cell",
                                        idx === columns.length - 1 && "mr-1"
                                    )}
                                    style={
                                        isFirstCol && row.depth > 0
                                            ? { paddingLeft: `${row.depth * SUBTASK_INDENT_WIDTH}px` }
                                            : undefined
                                    }
                                >
                                    {isFirstCol && row.hasSubtasks && (
                                        <button
                                            type="button"
                                            className="flex items-center justify-center w-5 h-5 text-zinc-400 hover:text-zinc-600 cursor-pointer top-1/2 -translate-y-1/2 absolute z-50 focus-visible:outline-none"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onToggleSubtasks(row.key);
                                            }}
                                        >
                                            <ChevronRight
                                                className={cn(
                                                    "size-3.5 transition-transform duration-150",
                                                    isSubtasksExpanded && "rotate-90"
                                                )}
                                            />
                                        </button>
                                    )}
                                    {cell.cell({ row })}
                                </div>
                            );
                        })}
                    </div>
                }
            ></ContextMenuTrigger>
            <ContextMenuContent className="w-52">
                <ContextMenuItem onClick={handleEditTitle}>
                    <Pencil />
                    Edit title
                </ContextMenuItem>
                <ContextMenuItem onClick={handleOpenInPanel}>
                    <PanelRightOpen />
                    Open in panel
                </ContextMenuItem>
                <ContextMenuSeparator />
                <ContextMenuItem onClick={handleCopyTaskId}>
                    <ClipboardCopy />
                    Copy task ID
                </ContextMenuItem>
                <ContextMenuItem onClick={handleCopyTaskTitle}>
                    <ClipboardCopy />
                    Copy task title
                </ContextMenuItem>
                <ContextMenuItem onClick={handleCopyTaskLink}>
                    <LinkIcon />
                    Copy task link
                </ContextMenuItem>
                <ContextMenuSeparator />
                <ContextMenuItem onClick={handleDeleteTask}>
                    <Trash2 />
                    Delete task
                </ContextMenuItem>
            </ContextMenuContent>
        </ContextMenu>
    );
}

/**
 * The table rebuilds its model — and therefore every `Row` instance — whenever the
 * task list changes, so comparing `row` by identity makes the memo useless: one
 * edit anywhere re-renders every visible row and all of its cells.
 *
 * Compare the parts of `row` this component actually renders instead. `row.data`
 * is the task object straight out of the query cache, which immer keeps
 * referentially stable for tasks that didn't change, so an edit to one task now
 * re-renders one row. When this returns true the cells keep the previous `Row`
 * instance, which is safe precisely because `data`, `depth` and `hasSubtasks` are
 * known to be identical — nothing rendered from it can be stale.
 *
 * `row.group` and `row.subtaskRows` are deliberately not compared: neither this
 * component nor the cells read them (the table uses them to build its flat row
 * list). If a cell ever starts reading one, add it here.
 */
export function taskTableTaskRowPropsAreEqual(prev: TaskTableTaskRowProps, next: TaskTableTaskRowProps) {
    return (
        prev.row.key === next.row.key &&
        prev.row.data === next.row.data &&
        prev.row.depth === next.row.depth &&
        prev.row.hasSubtasks === next.row.hasSubtasks &&
        prev.columns === next.columns &&
        prev.isFirst === next.isFirst &&
        prev.isLast === next.isLast &&
        prev.isSubtasksExpanded === next.isSubtasksExpanded &&
        prev.isNestTarget === next.isNestTarget &&
        prev.dropLinePosition === next.dropLinePosition &&
        prev.dropLineDepth === next.dropLineDepth &&
        prev.isDragSource === next.isDragSource &&
        prev.onRowClick === next.onRowClick &&
        prev.onToggleSubtasks === next.onToggleSubtasks &&
        prev.deleteTaskAlertDialogRef === next.deleteTaskAlertDialogRef
    );
}

const TaskTableTaskRow = memo(TaskTableTaskRowInner, taskTableTaskRowPropsAreEqual);

export default TaskTableTaskRow;
